import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { TranslateResult } from 'vue-i18n'
import { VueConstructor } from 'vue'
import { mapGetters } from 'vuex'
import { BButton, BDropdownItem, BAlert, BModal, BOverlay } from 'bootstrap-vue'
import { SetupIntent } from '@stripe/stripe-js'
import { StripeElements } from '@stripe/stripe-js/types/stripe-js/elements-group'
import { KForm, KFormSpinner } from '@kwixeo/form-vue'
import { Spinner } from '@kwixeo/interface-vue'
import StripeMixin from '../../mixins/StripeMixin'
import PaymentMethodSuccessfullyUpdated from '../PaymentMethodSuccessfullyUpdated/PaymentMethodSuccessfullyUpdated.vue'

@Component({
  components: {
    BOverlay, BButton, BModal, BAlert,
    KForm, KFormSpinner,
    Spinner,
    PaymentMethodSuccessfullyUpdated
  },
  computed: {
    ...mapGetters('user', ['team']),
  }
})
export default class PaymentMethodUpdate extends Mixins(StripeMixin) {
  team!: KwixeoFramework.Team

  @Prop({ default: 'button' }) element!: 'button'|'dropdown'|'modal'
  @Prop({ default: null }) label!: TranslateResult|string
  @Prop({ default: null }) btnClass!: KwixeoInterface.CssClass|string|null
  @Prop({ default: null }) size!: string|null
  @Prop({ default: null }) variant!: string|null
  @Prop({ default: false }) disabled!: boolean
  @Prop({ default: false }) modal!: boolean

  private loading = false
  private errorMsg: string|null = null
  private modalState = false
  private paymentItentId: string|null = null
  private clientSecret: string|null = null
  private elements: StripeElements|null = null
  private setupIntent: SetupIntent|null = null

  private async getPaymentIntent (): Promise<void> {
    return this.$kwSubscriptions.axios.put('/team/subscription/update-card')
      .then(response => {
        const datas = response.data
        this.paymentItentId = datas.payment_ident_id
        this.clientSecret = datas.client_secret
      })
      .catch(error => {
        throw new Error(error)
      })
  }

  // Initialize the payment form
  private initializePaymentForm (): void {
    if (!this.stripe || !this.clientSecret) {
      return
    }
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance: {
        variables: {
          fontSizeBase: '.9rem'
        }
      }
    })
    const paymentElement = this.elements.create('payment', {
      fields: {
        billingDetails: 'never'
      },
      wallets: {
        applePay: 'never',
        googlePay: 'never'
      }
    })
    paymentElement.mount(this.$refs.paymentElement as HTMLDivElement)
  }

  get component (): VueConstructor|null {
    switch (this.element) {
      case 'button':
        return BButton
      case 'dropdown':
        return BDropdownItem
      default:
        return null
    }
  }

  get buttonLabel (): TranslateResult|string {
    return this.label ?? this.$t('interface.edit')
  }

  @Watch('modal', { immediate: true })
  private watchModal (state: boolean): void {
    this.modalState = state
  }

  @Watch('modalState')
  private async watchModalState (): Promise<void> {
    if (!this.modalState) {
      return
    }

    this.loading = true
    this.setupIntent = null
    if (!this.paymentItentId) {
      await this.getPaymentIntent()
    }

    if (!this.stripe) {
      await this.initializeStripe()
    }

    this.loading = false
    this.$nextTick(() => {
      this.initializePaymentForm()
    })
  }

  private async process (): Promise<void> {
    const stripe = this.stripe
    const elements = this.elements

    if (!stripe || !elements) {
      this.errorMsg = 'Erreur lors de l\'initialisation de l\'envoi'
      return
    }

    this.loading = true
    const teamSubscription = this.team.subscription as KwixeoSubscriptions.TeamSubscription
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.protocol + '//' + window.location.host + '/configuration/subscription/payment-method/checkout',
        payment_method_data: {
          billing_details: {
            address: {
              city: teamSubscription.town ?? '',
              country: teamSubscription.country?.code,
              line1: teamSubscription.road1 ?? '',
              line2: teamSubscription.road2 ?? '',
              postal_code: teamSubscription.postal_code ?? '',
              state: ''
            },
            email: '',
            name: teamSubscription.name ?? '',
            phone: ''
          }
        }
      }
    })
    this.loading = false
    this.paymentItentId = null
    this.clientSecret = null
    this.elements = null

    if (error) {
      this.errorMsg = error.message ?? 'Une erreur est survenue lors de l\'ajout du moyen de paiement'
      return
    } else if (setupIntent) {
      this.setupIntent = setupIntent
    }
  }

  private closeModal (): void {
    this.$emit('closed')
  }
}
